<template>
<div :class="isFullPage ? 'nch--full-html-wrap' : 'nch--custom-html-page container'">
    <!-- Full page: iframe isolates JS/CSS and can override platform layout -->
    <iframe
        v-if="isFullPage"
        ref="htmlFrame"
        :srcdoc="config.html || ''"
        class="nch--full-html-iframe"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        frameborder="0"
        @load="onFrameLoad"
    ></iframe>

    <!-- Integrated: rendered inline with platform styles -->
    <div v-else class="nch--prose" v-html="config.html || ''"></div>
</div>
</template>

<script>
export default {
    name: 'CustomHtmlPage',
    props: {
        config: { type: Object, default: () => ({}) },
    },
    computed: {
        isFullPage() {
            return !!(this.config && this.config.full_page);
        },
    },
    methods: {
        onFrameLoad() {
            // Auto-resize iframe to match content height
            const frame = this.$refs.htmlFrame;
            if (!frame) return;
            try {
                const h = frame.contentDocument.documentElement.scrollHeight;
                if (h > 200) frame.style.height = h + 'px';
            } catch (e) {
                // Cross-origin: fallback to 100vh
            }
        },
    },
};
</script>

<style scoped>
.nch--full-html-wrap {
    width: 100%;
    padding: 0;
    margin: 0;
}

.nch--full-html-iframe {
    display: block;
    width: 100%;
    min-height: 100vh;
    border: none;
    background: #fff;
}

.nch--custom-html-page {
    padding: 2.5rem 1rem;
}

/* Deep selector: styles v-html injected content */
.nch--prose {
    line-height: 1.75;
    color: var(--page-text, #212121);
    font-size: 1rem;
    max-width: 800px;
    margin: 0 auto;
    word-break: break-word;
}

.nch--prose :deep(h1),
.nch--prose :deep(h2),
.nch--prose :deep(h3),
.nch--prose :deep(h4),
.nch--prose :deep(h5),
.nch--prose :deep(h6) {
    font-weight: 700;
    line-height: 1.3;
    margin-top: 2em;
    margin-bottom: 0.6em;
    color: var(--page-text, #111);
}

.nch--prose :deep(h1) { font-size: 2rem; }
.nch--prose :deep(h2) { font-size: 1.6rem; }
.nch--prose :deep(h3) { font-size: 1.3rem; }
.nch--prose :deep(h4) { font-size: 1.1rem; }
.nch--prose :deep(h5) { font-size: 1rem; font-weight: 600; }
.nch--prose :deep(h6) { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.06em; color: #757575; }

.nch--prose :deep(p) {
    margin-bottom: 1.2em;
}

.nch--prose :deep(a) {
    color: #1976d2;
    text-decoration: none;
    border-bottom: 1px solid rgba(25, 118, 210, 0.3);
    transition: border-color 0.15s;
}

.nch--prose :deep(a:hover) {
    border-color: #1976d2;
}

.nch--prose :deep(ul),
.nch--prose :deep(ol) {
    padding-left: 1.5em;
    margin-bottom: 1.2em;
}

.nch--prose :deep(li) {
    margin-bottom: 0.35em;
}

.nch--prose :deep(blockquote) {
    border-left: 4px solid var(--page-accent, #1976d2);
    padding: 0.75em 1.25em;
    margin: 1.5em 0;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 0 6px 6px 0;
    color: var(--page-text-secondary, #424242);
    font-style: italic;
}

.nch--prose :deep(code) {
    font-family: 'Fira Code', 'Courier New', monospace;
    font-size: 0.875em;
    background: var(--page-surface, #f5f5f5);
    padding: 0.15em 0.4em;
    border-radius: 3px;
    color: var(--page-accent, #c62828);
}

.nch--prose :deep(pre) {
    background: #1e1e2e;
    color: #cdd6f4;
    padding: 1.25em;
    border-radius: 8px;
    overflow-x: auto;
    margin: 1.5em 0;
    font-size: 0.875em;
    line-height: 1.6;
}

.nch--prose :deep(pre code) {
    background: none;
    color: inherit;
    padding: 0;
    font-size: inherit;
}

.nch--prose :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5em 0;
    font-size: 0.92em;
}

.nch--prose :deep(table th) {
    background: var(--page-surface, #f5f5f5);
    font-weight: 700;
    text-align: left;
    padding: 10px 14px;
    border-bottom: 2px solid var(--page-border, #e0e0e0);
    white-space: nowrap;
    color: var(--page-text, inherit);
}

.nch--prose :deep(table td) {
    padding: 9px 14px;
    border-bottom: 1px solid var(--page-border, #f0f0f0);
    vertical-align: top;
    color: var(--page-text, inherit);
}

.nch--prose :deep(table tr:hover td) {
    background: var(--page-surface, #fafafa);
}

.nch--prose :deep(img) {
    max-width: 100%;
    border-radius: 6px;
    margin: 1em 0;
    display: block;
}

.nch--prose :deep(hr) {
    border: none;
    border-top: 1px solid var(--page-border, #e0e0e0);
    margin: 2.5em 0;
}

.nch--prose :deep(figcaption) {
    font-size: 0.82em;
    color: var(--page-text-secondary, #9e9e9e);
    text-align: center;
    margin-top: 0.4em;
}

.nch--prose :deep(strong) { font-weight: 700; }
.nch--prose :deep(em) { font-style: italic; }

.nch--prose :deep(figure) {
    margin: 1.5em 0;
}

.nch--prose :deep(figcaption) {
    font-size: 0.82em;
    color: #9e9e9e;
    text-align: center;
    margin-top: 0.4em;
}
</style>
